import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { storeCatalogApi } from '../api/store-catalog-api';

import { storeCatalogKeys } from './store-catalog-keys';
import { REASSERTS, type RotationOwner } from './use-rotation-owners';

/**
 * The rotations targeting a store that would revert a manual status change at their next run,
 * for a selection of "all matching" whose rows are not loaded, so which of them a rotation owns
 * is not known (`count` null). A first page of 20, cached briefly; null when they cannot be read,
 * so the caller errs on the side of warning.
 */
export function useStoreRotations() {
  const queryClient = useQueryClient();
  return useCallback(
    async (storeId: string): Promise<RotationOwner[] | null> => {
      try {
        const page = await queryClient.query({
          queryKey: storeCatalogKeys.scheduleOptions(storeId, ''),
          queryFn: () => storeCatalogApi.schedules(storeId, ''),
          staleTime: 30_000,
        });
        return page.items
          .filter((schedule) => REASSERTS.has(schedule.status))
          .map((schedule) => ({
            id: schedule.id,
            name: schedule.name,
            nextRunAt: schedule.nextRunAt,
            count: null,
          }));
      } catch {
        return null;
      }
    },
    [queryClient],
  );
}
